import React, { FC, useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Box, Card, CardContent, Typography, Stepper, Step, StepLabel } from '@mui/material';

import {
  callModalAppWarning,
  callModalAppWarningMobile,
  callModalBody,
  callModalBodyMobile,
  callModalCard,
  callModalCardContent,
  callModalCardContentMobile,
  callModalCardMobile,
  callModalPaperMobile,
  callModalStatusText,
  callModalStatusTextMobile,
  callModalStepper,
  callModalStepperMobile,
  callModalStyle,
  callModalTitle,
  callModalTitleMobile
} from '../../../../shared/lib/theme/styleConstants';
import { useMobileView } from '../../app/hooks/useMobileView';
import { CallModalProps } from '../../app/interfaceTypes';
import { RingMyPhoneLogs, TwillioCallStatus, useFetchRingMyPhoneLogLazyQuery } from '../../generated';
import { Alert } from '../../../../shared/lib/common/Alert';
import { BUTTON_TEXT_CLOSE, LABEL_RING_MY_PHONE_TM } from '../../../../shared/lib/constants';
import { withTrademark } from '../../../../shared/lib/common/Tokenizer';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';

const CallModal: FC<CallModalProps> = ({ open, onClose, ringMyPhoneLog, showApp }) => {
  const [ringLog, setRingLog] = useState<RingMyPhoneLogs>();
  const mobileView = useMobileView();

  const steps = [TwillioCallStatus.Queued, TwillioCallStatus.Ringing, 'ANSWERED', 'COMPLETED'];

  const getActiveStep = (status: TwillioCallStatus) => {
    switch (status) {
      case TwillioCallStatus.Queued:
        return 0;

      case TwillioCallStatus.Ringing:
        return 1;
      case TwillioCallStatus.InProgress:
        return 2;

      case TwillioCallStatus.Completed:
        return 3;

      case TwillioCallStatus.Busy:
      case TwillioCallStatus.Failed:
      case TwillioCallStatus.Canceled:
      case TwillioCallStatus.NoAnswer:
        return -1;

      default:
        return 0;
    }
  };

  const getStatusText = (status: TwillioCallStatus) => {
    switch (status) {
      case TwillioCallStatus.Queued:
        return 'Call Queued';

      case TwillioCallStatus.Ringing:
        return 'Phone is Ringing...';

      case TwillioCallStatus.Busy:
        return 'User Busy';

      case TwillioCallStatus.Canceled:
        return 'Call Canceled';

      case TwillioCallStatus.Failed:
        return 'Failed to Connect';

      case TwillioCallStatus.NoAnswer:
        return 'No Answer';

      case TwillioCallStatus.InProgress:
        return 'Calling In Progress...';

      case TwillioCallStatus.Completed:
        return 'Completed Successfully!';

      default:
        return 'Waiting for updates...';
    }
  };

  const getIcon = (status: TwillioCallStatus, size = 55) => {
    switch (status) {
      case TwillioCallStatus.Queued:
        return <PhoneIphoneIcon color="warning" sx={{ fontSize: size }} />;

      case TwillioCallStatus.Ringing:
        return <CallMadeIcon color="secondary" sx={{ fontSize: size }} />;

      case TwillioCallStatus.InProgress:
        return <CallMadeIcon color="info" sx={{ fontSize: size }} />;

      case TwillioCallStatus.Completed:
        return <CheckCircleIcon color="success" sx={{ fontSize: size }} />;

      case TwillioCallStatus.Failed:
        return <ErrorIcon color="error" sx={{ fontSize: size }} />;

      case TwillioCallStatus.Busy:
        return <CancelIcon color="warning" sx={{ fontSize: size }} />;

      case TwillioCallStatus.NoAnswer:
        return <CancelIcon color="warning" sx={{ fontSize: size }} />;

      case TwillioCallStatus.Canceled:
        return <CancelIcon color="error" sx={{ fontSize: size }} />;

      default:
        return <PhoneIphoneIcon sx={{ fontSize: size }} />;
    }
  };

  const getProgressText = (status: TwillioCallStatus) => {
    switch (status) {
      case TwillioCallStatus.Queued:
        return 'Preparing call...';

      case TwillioCallStatus.Ringing:
        return 'Connecting to your phone...';

      case TwillioCallStatus.InProgress:
        return 'Call in progress...';

      case TwillioCallStatus.Completed:
        return 'Call completed successfully!';

      case TwillioCallStatus.Failed:
        return 'Unable to complete the call';

      case TwillioCallStatus.Busy:
        return 'Your line was busy';

      case TwillioCallStatus.NoAnswer:
        return 'No answer received';

      case TwillioCallStatus.Canceled:
        return 'Call was canceled';

      default:
        return 'Waiting for updates...';
    }
  };

  const [fetchRingMyPhoneLog] = useFetchRingMyPhoneLogLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const { fetchRingMyPhoneLog } = data;
      if (fetchRingMyPhoneLog) {
        setRingLog(fetchRingMyPhoneLog);
      }
    },
    onError: ({ message }) => {
      Alert.error(message);
    }
  });

  useEffect(() => {
    if (!ringMyPhoneLog?.id) return;

    let pollCount = 0;
    const maxPolls = 10;
    const terminalStatuses = [TwillioCallStatus.Completed, TwillioCallStatus.Failed, TwillioCallStatus.Canceled, TwillioCallStatus.NoAnswer, TwillioCallStatus.Busy];

    const fetchLog = () => {
      fetchRingMyPhoneLog({
        variables: { FetchRingMyPhoneLogInput: { id: ringMyPhoneLog.id } }
      }).then((res) => {
        pollCount++;
        const status = res?.data?.fetchRingMyPhoneLog?.status;
        if (status && (terminalStatuses.includes(status) || pollCount >= maxPolls)) {
          clearInterval(interval);
        }
      });
    };

    // Initial fetch
    fetchLog();

    // Poll every 15 seconds
    const interval = setInterval(fetchLog, 10000);

    return () => clearInterval(interval);
  }, [ringMyPhoneLog?.id]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={callModalStyle} PaperProps={mobileView ? { sx: callModalPaperMobile } : undefined}>
      <DialogTitle sx={mobileView ? callModalTitleMobile : callModalTitle}>{withTrademark(LABEL_RING_MY_PHONE_TM)}</DialogTitle>

      <DialogContent dividers>
        {showApp && (
          <Box
            mt={mobileView ? 1 : 3}
            p={mobileView ? 1.5 : 2}
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={mobileView ? callModalAppWarningMobile : callModalAppWarning}
          >
            <ErrorIcon color="warning" sx={{ mr: 1 }} />
            <Typography variant="body2" fontWeight={600}>
              App Notification did not reach the device — WiFi may be off or the app is not installed.
            </Typography>
          </Box>
        )}

        <Box sx={mobileView ? callModalBodyMobile : callModalBody}>
          <Card sx={mobileView ? callModalCardMobile : callModalCard}>
            <CardContent sx={mobileView ? callModalCardContentMobile : callModalCardContent}>
              <Box mb={mobileView ? 1 : 2}>{getIcon(ringLog?.status || TwillioCallStatus.Queued, mobileView ? 40 : 55)}</Box>

              {/* Status Text */}
              <Typography variant="h6" fontWeight={700} sx={mobileView ? callModalStatusTextMobile : callModalStatusText}>
                {getIcon(ringLog?.status || TwillioCallStatus.Queued, mobileView ? 18 : 20)}
                {getStatusText(ringLog?.status || TwillioCallStatus.Queued)}
              </Typography>

              <Typography variant="body2" sx={{ opacity: 0.7, mt: 1 }}>
                {getProgressText(ringLog?.status || TwillioCallStatus.Queued)}
              </Typography>

              {/* Stepper */}
              <Stepper activeStep={getActiveStep(ringLog?.status || TwillioCallStatus.Queued)} alternativeLabel sx={mobileView ? callModalStepperMobile : callModalStepper}>
                {steps.map((label, index) => (
                  <Step key={label} completed={index === steps.length - 1 ? ringLog?.status === TwillioCallStatus.Completed : undefined}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </CardContent>
          </Card>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          justifyContent: 'flex-end'
        }}
      >
        <Button onClick={onClose} variant="contained" color="primary">
          {BUTTON_TEXT_CLOSE}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CallModal;
